const EVENT_STOP_DRAG = 'stop_drag';
const EVENT_START_DRAG = 'start_drag';
const EVENT_DRAG = 'drag';
const EVENT_DRAGGED = 'dragged';
const EVENT_FIRST_DRAG = 'drag';
const EVENT_CLICK = 'click';

/**
 * @class DragManager
 * */
export default class DragManager {
  /*
  * @constructor
  * @param {Object} props
  * Supported props:
  * - onStartDrag(event) {function}: called when starting the drag (usually on press)
  * - isDrag(dragInfo, previousDragInfo, startDragInfo) {function}: (optional) called to check whether the first pointer movement is concidered a drag
  *   By default, returns true on the first pointer event
  * - onFirstDrag(event) {function}: callend when the first drag happened between the startDrag & stopDrag
  * - onDrag(event) {function}: called on each pointer movement event between startDrag & stopDrag
  * - onDragged(event) {function}: called on release when pointer movement occurred
  * - onStopDrag(event) {function}: called when the drag stopped (usually on release)
  * - isClick({startDragInfo, dragInfo, stopDragInfo, dragged, dragData}) {function}: called to check wheter a click occurred
  *     By default, a click is defined as a startDrag and stopDrag without pointer movement inbetween
  * - onClick(event) {function}: called when a startDrag & stopDrag happened but no pointer movement inbetween
  * - stopDragOnRelease {boolean} (default = true) If true, automatically stop dragging on release
  */
  constructor(props) {
    this.dragHandler = this.dragHandler.bind(this);
    this.releaseHandler = this.releaseHandler.bind(this);
    this.startDrag = this.startDrag.bind(this);
    this.stopDrag = this.stopDrag.bind(this);

    this.props = props;
  }

  setListener(name, func) {
    if (!name) {
      return;
    }
    const props = this.props || {};
    this.props = props;

    props[name] = func;
  }

  getEventObject(type) {
    const map = this.eventMap || {};
    this.eventMap = map;
    const evt = map[type] || {};
    map[type] = evt;

    evt.type = type;

    return evt;
  }

  cloneInfo(info, result = null) {
    const res = result || {};

    let evt = null;
    let type = null;
    let resultTouches = null;
    let numTouches = null;
    let resultTouch = null;
    let x = 0;
    let y = 0;
    let target = null;
    let currentTarget = null;

    if (info) {
      evt = info.originalEvent;
      type = info.type;
      numTouches = info.numTouches;
      resultTouches = info.touches;
      resultTouch = info.pointerData;
      x = info.x;
      y = info.y;
      target = info.target;
      currentTarget = info.currentTarget;
    }

    res.originalEvent = evt;
    res.type = type;
    res.touches = resultTouches;
    res.numTouches = numTouches;
    res.pointerData = resultTouch;
    res.x = x;
    res.y = y;
    res.target = target;
    res.currentTarget = currentTarget;

    return res;
  }

  getTouchesFromEvent(evt) {
    if (!evt) {
      return null;
    }
    const { touches, changedTouches } = evt;
    if (changedTouches && changedTouches.length > 0) {
      return changedTouches;
    }
    if (touches && touches.length > 0) {
      return touches;
    }
    return null;
  }

  getCoordDataFromEvent(evt, index = 0) {
    if (!evt) {
      return null;
    }
    const touches = this.getTouchesFromEvent(evt);
    if (touches) {
      return touches[index];
    }

    return evt;
  }

  getInfoFromEvent(evt, result = null) {
    const res = result || {};

    let clientX = 0;
    let clientY = 0;
    let numPointers = 0;
    let resultPointers = null;
    let resultPointer = null;
    let target = null;
    let currentTarget = null;
    let type = null;
    if (evt) {
      type = evt.type;

      const touches = this.getTouchesFromEvent(evt);
      resultPointer = evt;
      numPointers = 1;
      if (touches) {
        resultPointers = touches;
        resultPointer = touches[0];
        numPointers = touches.length;
      }

      target = resultPointer.target;
      currentTarget = resultPointer.currentTarget;

      clientX = resultPointer.clientX || 0;
      clientY = resultPointer.clientY || 0;
    }

    res.originalEvent = evt;
    res.type = type;
    res.pointers = resultPointers;
    res.numPointers = numPointers;
    res.pointerData = resultPointer;
    res.x = clientX;
    res.y = clientY;
    res.target = target;
    res.currentTarget = currentTarget;
    return res;
  }

  getDragSession() {
    const dragSession = this.dragSession || {};
    this.dragSession = dragSession;
    return dragSession;
  }

  dragHandler(evt) {
    const e = evt || window.event;
    const dragSession = this.getDragSession();
    dragSession.dragInfo = this.getInfoFromEvent(e, dragSession.dragInfo);
    const { props } = this;

    let { dragged } = dragSession;
    const { dragInfo } = dragSession;

    if (!dragged) {
      if (!props || !props.isDrag || props.isDrag(dragSession.dragInfo, dragSession.prevDragInfo, dragSession.startDragInfo) !== false) {
        dragged = true;
        dragSession.dragged = dragged;
        dragInfo.dragged = dragged;
        if (props && props.onFirstDrag) {
          const resEvt = this.getEventObject(EVENT_FIRST_DRAG);

          resEvt.data = dragSession.dragData;
          resEvt.info = dragSession.dragInfo;
          resEvt.previousInfo = dragSession.prevDragInfo;
          resEvt.startInfo = dragSession.startDragInfo;

          props.onFirstDrag(resEvt);
        }
      }
    }

    if (dragged && props && props.onDrag) {
      const resEvt = this.getEventObject(EVENT_DRAG);

      resEvt.data = dragSession.dragData;
      resEvt.info = dragSession.dragInfo;
      resEvt.previousInfo = dragSession.prevDragInfo;
      resEvt.startInfo = dragSession.startDragInfo;

      props.onDrag(resEvt);
    }
    if (dragged) {
      dragSession.prevDragInfo = this.cloneInfo(dragSession.dragInfo, dragSession.prevDragInfo);
    }
    return this.getEventResult(evt);
  }

  releaseHandler(evt) {
    const e = evt || window.event;
    this.stopDrag(e);

    return this.getEventResult(evt);
  }

  /**
  * @method startDrag
  * @param {Object} evt - Mouse / touch event
  * @param {Object} data - optional object containing arbitrary data
  */
  startDrag(evt = null, data = null) {
    const dragSession = this.getDragSession();

    dragSession.startDragInfo = this.getInfoFromEvent(evt, dragSession.startDragInfo);
    dragSession.prevDragInfo = this.cloneInfo(dragSession.startDragInfo, dragSession.prevDragInfo);
    dragSession.dragInfo = this.getInfoFromEvent(null, dragSession.dragInfo);
    dragSession.stopDragInfo = this.getInfoFromEvent(null, dragSession.stopDragInfo);
    dragSession.dragged = false;

    dragSession.dragData = data;
    dragSession.dragInfo.dragged = false;

    const { props } = this;

    if (props && props.onStartDrag) {
      const resEvt = this.getEventObject(EVENT_START_DRAG);
      resEvt.data = data;
      resEvt.info = dragSession.startDragInfo;
      resEvt.canceled = false;
      props.onStartDrag(resEvt);
      if (resEvt.canceled) {
        // onStartDrag caused the event to be canceled
        // -> no dragging
        return this.eventResult(evt, false);
      }
    }
    const target = window;
    this.setTargetEventListener(target, 'mousemove', this.dragHandler, true);
    this.setTargetEventListener(target, 'touchmove', this.dragHandler, true);
    if (props.stopDragOnRelease !== false) {
      this.setTargetEventListener(target, 'mouseup', this.releaseHandler, true);
      this.setTargetEventListener(target, 'touchend', this.releaseHandler, true);
    }

    return this.getEventResult(evt);
  }

  stopDrag(evt = null) {
    const target = window;
    this.setTargetEventListener(target, 'mousemove', this.dragHandler, false);
    this.setTargetEventListener(target, 'touchmove', this.dragHandler, false);
    this.setTargetEventListener(target, 'mouseup', this.releaseHandler, false);
    this.setTargetEventListener(target, 'touchend', this.releaseHandler, false);

    const dragSession = this.getDragSession();
    dragSession.stopDragInfo = this.getInfoFromEvent(evt, dragSession.stopDragInfo);
    const { props } = this;
    if (props && props.onStopDrag) {
      const { dragged } = dragSession;
      const resEvt = this.getEventObject(EVENT_STOP_DRAG);
      resEvt.data = dragSession.dragData;
      resEvt.info = dragSession.stopDragInfo;
      resEvt.lastDragInfo = dragSession.prevDragInfo;
      resEvt.startInfo = dragSession.startDragInfo;
      resEvt.dragged = dragged;
      props.onStopDrag(resEvt);
    }
    if (this.isClick()) {
      if (props && props.onClick) {
        const resEvt = this.getEventObject(EVENT_CLICK);
        props.onClick(resEvt);
      }
    } else if (props && props.onDragged) {
      const resEvt = this.getEventObject(EVENT_DRAGGED);
      props.onDragged(resEvt);
    }
  }

  isClick() {
    const dragSession = this.getDragSession();

    const {
      dragged,
    } = dragSession;

    const { isClick } = this.props;
    if (!isClick) {
      // Default behaviour
      return !dragged;
    }

    const {
      startDragInfo,
      dragInfo,
      stopDragInfo,
      dragData,
    } = dragSession;

    return isClick({
      startDragInfo,
      dragInfo,
      stopDragInfo,
      dragged,
      dragData,
    });
  }

  setTargetEventListener(target, type, handler, add) {
    if (!target || !type || !handler) {
      return;
    }
    if (add) {
      if (target.addEventListener) {
        target.addEventListener(type, handler);
      } else if (target.attachEvent) {
        target.attachEvent(`on${type}`, handler);
      }
    } else if (target.removeEventListener) {
      target.removeEventListener(type, handler);
    } else if (target.detachEvent) {
      target.detachEvent(`on${type}`, handler);
    }
  }

  getEventResult(evt) {
    return this.eventResult(evt, false);
  }

  eventResult(evt, result) {
    if (!result) {
      if (evt && evt.preventDefault) {
        evt.preventDefault();
      }
    }
    return result;
  }

  static calculateDelta(info, prevInfo, result) {
    const res = result || {};

    const dx = info.x - prevInfo.x;
    const dy = info.y - prevInfo.y;

    res.deltaX = dx;
    res.deltaY = dy;

    return res;
  }
}



// WEBPACK FOOTER //
// ./js/utils/project-definition/drag-manager.js